import { useEffect, useRef } from "react";
import { clsx } from "clsx";
import { Link } from "react-router-dom";
import { HOST_ROUTE } from "../data.ts";
import { buildEventsNav, withUtm, MAIN_ORIGIN } from "../../../config/crossSiteNav.ts";

interface Props {
  open: boolean;
  onClose: () => void;
}

const NAV_ITEMS = buildEventsNav();
const GET_STARTED_HREF = withUtm(`${MAIN_ORIGIN}/#lead-form`, "events", "header_cta");

const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function MobileNav({ open, onClose }: Props) {
  const panelRef = useRef<HTMLElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Stable ref so the effect dep array is [open] only — prevents the whole
  // effect (scroll-lock, focus, keydown listener) from tearing down and
  // re-running on every parent re-render while the drawer is open.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }); // sync every render, no deps

  useEffect(() => {
    if (!open) return;

    // Remember where focus was before opening
    previousFocusRef.current = document.activeElement;

    // Body scroll lock — preserve previous value for cleanup
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move initial focus to the close button
    closeBtnRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }

      if (e.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
      ).filter((el) => el.offsetParent !== null);

      if (focusable.length === 0) return;

      // noUncheckedIndexedAccess: assert non-null after length guard
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (e.shiftKey) {
        // Shift+Tab: wrap from first → last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: wrap from last → first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the element that opened the drawer
      if (
        previousFocusRef.current &&
        typeof (previousFocusRef.current as HTMLElement).focus === "function"
      ) {
        (previousFocusRef.current as HTMLElement).focus();
      }
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_.2s_ease-out]"
      />
      {/* Panel */}
      <aside
        ref={panelRef}
        data-testid="mobile-nav-panel"
        className="absolute right-0 top-0 flex h-full w-72 flex-col bg-ink shadow-2xl animate-[slideInRight_.28s_ease-out]"
      >
        <div className="flex items-center justify-between border-b border-white/[.08] px-5 py-4">
          <span className="font-mono text-xs font-medium uppercase tracking-[.24em] text-flame">RestoMag · Events</span>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="flex h-9 w-9 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
          {NAV_ITEMS.map((item) => {
            const cls = clsx(
              "rounded-lg px-4 py-3 text-sm font-medium transition-colors",
              item.selfActive ? "bg-white/[.08] text-white" : "text-white/70 hover:bg-white/5 hover:text-white",
            );
            return item.external ? (
              <a key={item.label} href={item.href} onClick={onClose} className={cls} aria-current={item.selfActive ? "page" : undefined}>{item.label}</a>
            ) : (
              <Link key={item.label} to={item.href} onClick={onClose} className={cls} aria-current={item.selfActive ? "page" : undefined}>{item.label}</Link>
            );
          })}
        </nav>
        <div className="flex flex-col gap-3 border-t border-white/[.08] px-5 py-5">
          <a
            href={GET_STARTED_HREF}
            onClick={onClose}
            className="rounded-xl border border-flame bg-flame px-5 py-3 text-center text-sm font-bold text-white no-underline"
          >
            Get Started
          </a>
          <Link
            to={HOST_ROUTE}
            onClick={onClose}
            className="rounded-xl border border-white/[.24] bg-white/[.06] px-5 py-3 text-center text-sm font-semibold text-white no-underline"
          >
            Host an Event
          </Link>
        </div>
      </aside>
    </div>
  );
}
